
import { Component, Prop, Ref, Vue } from 'vue-property-decorator'
import ModalUserPassword from '@/components/templates/ModalUserPassword.vue'
import { UsersApi } from '@/api/methods/UsersApi'
import { ApiConflictAppError } from '@/models/AppErrorClasses'
import PasswordUtils from '@/utils/PasswordUtils'
import { UserRole } from '@/const/UserRole'

/**
 * パスワード変更モーダル
 */
@Component({
  components: {
    ModalUserPassword,
  },
})
export default class ModalEditUserPassword extends Vue {
  /** パスワード変更モーダルのフォーマット */
  @Ref()
  modalUserPassword!: ModalUserPassword

  /** 権限 */
  @Prop()
  private role!: string

  /**
   * 変更ボタン押下時の処理
   *
   * @param currentPassword 現在のパスワード
   * @param newPassword 新しいパスワード
   * @param confirm 確認用
   */
  private async submitPassword(currentPassword: string, newPassword: string, confirm: string): Promise<void> {
    this.$loading.start()

    let errorMessage = null

    // パスワードのバリデーションチェック
    errorMessage = this.passwordValidation(currentPassword, newPassword, confirm)
    if (errorMessage) {
      alert(errorMessage)
      this.$loading.complete()
      return
    }

    try {
      // パスワード更新
      await UsersApi.updatePassword(currentPassword, newPassword)
    } catch (error) {
      if (error instanceof ApiConflictAppError) {
        alert('現在のパスワードが一致しません。再入力してください。')
        this.$loading.complete()
        return
      }

      // 409エラー以外の場合は例外を投げ直し、共通処理に渡す
      this.$loading.complete()
      throw error
    }

    this.$loading.complete()
    this.hide()

    // モーダルを閉じた後、Cookieを削除しログイン画面へ遷移
    alert('パスワードを変更しました。変更後のパスワードで再度ログインしてください。')
    this.$appCookies.removeAuth()

    // ログインページへ遷移
    await this.transitionRoleLogin()
  }

  /**
   * パスワードの入力検証(未入力、不一致など各検証項目をチェック)、及び入力値が有効か判定
   *
   * @param currentPassword 現在のパスワード
   * @param newPassword 新しいパスワード
   * @param confirm 確認用
   * @returns 検証項目に該当：エラーメッセージ 全ての検証をパス：null
   */
  public passwordValidation(currentPassword: string, newPassword: string, confirm: string): string | null {
    // 現在のパスワード未入力
    if (!currentPassword) {
      return '現在のパスワードを入力して下さい'
    }

    // 新規パスワード未入力
    if (!newPassword) {
      return '新規パスワードを入力して下さい'
    }

    // 現在のパスワードと新規パスワードが一致
    if (newPassword === currentPassword) {
      return '新規パスワードに、現在のパスワードと同じ値は使用できません'
    }

    // 新規パスワードと確認用パスワードが一致しない
    if (newPassword !== confirm) {
      return '新規パスワードと確認用パスワードが一致しません'
    }

    // パスワードが有効か判定
    return PasswordUtils.checkPasswordEnabled(newPassword)
  }

  /**
   * パスワード変更後のログインページ遷移を権限ごとに分岐する
   */
  private async transitionRoleLogin(): Promise<void> {
    if (this.role === UserRole.STUDENT) {
      await this.$router.push('/student/login')
    }
    if (this.role === UserRole.TEACHER || this.role === UserRole.BRANCH) {
      await this.$router.push('/teacher/login')
    }
    if (this.role === UserRole.ACADEMY) {
      await this.$router.push('/academy/login')
    }
    if (this.role === UserRole.ADMIN) {
      await this.$router.push('/admin/login')
    }
  }

  /**
   * モーダルを開く
   */
  public show(): void {
    this.modalUserPassword.show()
  }

  /**
   * モーダルを閉じる
   */
  private hide(): void {
    this.modalUserPassword.hide()
  }
}
